import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { 
  Package, Filter, Download, RefreshCw, Users, 
  ChevronDown, ChevronRight, Search
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#0066CC', '#00AA66', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function RelatorioStockDemografiaPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroArmazem, setFiltroArmazem] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    loadData();
  }, [filtroTipo, filtroArmazem]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroTipo) params.append('tipo_recurso_id', filtroTipo);
      if (filtroArmazem) params.append('armazem', filtroArmazem);
      
      const response = await apiClient.get(`/relatorios/stock/por-demografia?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (key) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedGroups(newExpanded);
  };

  const filteredData = data?.stock_por_demografia?.filter(g => 
    !searchTerm || 
    g.tipo_recurso?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.faixa_etaria_label?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Prepare chart data
  const chartByType = {};
  filteredData.forEach(g => {
    const tipo = g.tipo_recurso || 'Outros';
    chartByType[tipo] = (chartByType[tipo] || 0) + g.quantidade_total;
  });
  const chartTypeData = Object.entries(chartByType)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const chartByFaixa = {};
  filteredData.forEach(g => {
    const faixa = g.faixa_etaria_label || 'Não especificado';
    chartByFaixa[faixa] = (chartByFaixa[faixa] || 0) + g.quantidade_total;
  });
  const chartFaixaData = Object.entries(chartByFaixa)
    .map(([name, value]) => ({ name: name.split('(')[0].trim(), value }))
    .filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relatorio-stock-demografia">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Stock por Demografia</h1>
          <p className="text-slate-500">
            Recursos disponíveis agrupados por tipo, sexo, faixa etária e tamanho
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total de Itens</p>
                <p className="text-2xl font-bold text-blue-600">
                  {data?.resumo?.total_itens?.toLocaleString('pt-PT') || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Grupos Demográficos</p>
                <p className="text-2xl font-bold text-green-600">
                  {data?.resumo?.total_grupos || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Filter className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Data do Relatório</p>
                <p className="text-lg font-medium text-slate-700">
                  {data?.data_geracao ? new Date(data.data_geracao).toLocaleString('pt-PT') : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stock por Tipo de Recurso</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => value.toLocaleString('pt-PT')} />
                <Bar dataKey="value" fill="#0066CC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por Faixa Etária</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartFaixaData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {chartFaixaData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => value.toLocaleString('pt-PT')} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar tipo ou faixa etária..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroArmazem || "__all__"} onValueChange={(v) => setFiltroArmazem(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Armazém" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="Principal">Principal</SelectItem>
                <SelectItem value="Secundario">Secundário</SelectItem>
              </SelectContent>
            </Select>
            {(searchTerm || filtroArmazem) && (
              <Button 
                variant="ghost" 
                onClick={() => { setSearchTerm(''); setFiltroArmazem(''); }}
              >
                Limpar Filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhe por Grupo Demográfico</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Tipo de Recurso</TableHead>
                <TableHead>Sexo</TableHead>
                <TableHead>Faixa Etária</TableHead>
                <TableHead>Tamanho</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead className="text-right">Reservado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((grupo, idx) => {
                const key = `${grupo.tipo_recurso}-${grupo.sexo}-${grupo.faixa_etaria}-${grupo.tamanho}`;
                const isExpanded = expandedGroups.has(key);
                
                return (
                  <React.Fragment key={idx}>
                    <TableRow 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => toggleGroup(key)}
                    >
                      <TableCell>
                        {isExpanded ? 
                          <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        }
                      </TableCell>
                      <TableCell className="font-medium">{grupo.tipo_recurso}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          grupo.sexo === 'M' ? 'border-blue-300 text-blue-700' :
                          grupo.sexo === 'F' ? 'border-pink-300 text-pink-700' :
                          'border-slate-300'
                        }>
                          {grupo.sexo_label}
                        </Badge>
                      </TableCell>
                      <TableCell>{grupo.faixa_etaria_label || '-'}</TableCell>
                      <TableCell>{grupo.tamanho || '-'}</TableCell>
                      <TableCell className="text-right font-semibold text-blue-600">
                        {grupo.quantidade_total.toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right text-amber-600">
                        {grupo.quantidade_reservada?.toLocaleString('pt-PT') || 0}
                      </TableCell>
                    </TableRow>
                    {isExpanded && grupo.itens?.map((item, itemIdx) => (
                      <TableRow key={`${idx}-${itemIdx}`} className="bg-slate-50/50">
                        <TableCell></TableCell>
                        <TableCell colSpan={4} className="text-sm text-slate-600 pl-8">
                          {item.nome} 
                          {item.armazem && <span className="text-slate-400 ml-2">({item.armazem})</span>}
                          {item.condicao && <Badge variant="outline" className="ml-2 text-xs">{item.condicao}</Badge>}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {item.quantidade?.toLocaleString('pt-PT')}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Nenhum item encontrado com os filtros aplicados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
